import { useLayoutEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Header,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
} from "@repo/ui";
import {
  useGetExamtocChapterTopicsPublishedVideoAndFlashcardCount,
  useGetApiSelflearnGetTopicSummaryProgress,
} from "@oneapp/data";
import styles from "./TopicSummaryViewAllPage.module.scss";
import type { Topic, TopicProgress } from "../../../../types/reviseTypes";
import { buildTopicSummaryList } from "../../../../lib/topicSummaryUtils";
import { useScreenWidth } from "../../../../lib/useScreenWidth";
import { SELF_LEARN_ROUTES } from "../../../../routes";
import { TopicSummaryGrid } from "./components/TopicSummaryGrid";
import { TopicSummaryShimmerGrid } from "./components/TopicSummaryShimmerGrid";
import { ROUTESWEB, useIsMobile } from "@repo/utils";
import { useTheme } from "@repo/tokens";

const SELF_LEARN_BASE = `/${ROUTESWEB.APP.SELF_LEARN}`;
const CHAPTER_LIST_HREF = `${SELF_LEARN_BASE}/${SELF_LEARN_ROUTES.CHAPTERLIST}`;
const CHAPTER_DETAILS_HREF = `${SELF_LEARN_BASE}/${SELF_LEARN_ROUTES.CHAPTERLIST}/${SELF_LEARN_ROUTES.CHAPTERDETAILS}`;
const FLASHCARDS_HREF = `${SELF_LEARN_BASE}/${SELF_LEARN_ROUTES.FLASHCARDS}`;

export type TopicSummaryViewAllLocationState = {
  subjectId: number;
  subjectName: string;
  chapterId: number;
  chapterName: string;
  widgetTitle?: string;
  examId: number;
  gradeId: number;
  subTenantId: number;
};

export type AvatarItem = {
  id: number;
  label: string;
  onPress?: () => void;
};

export const TopicSummaryViewAllPage = () => {
  const { t } = useTranslation("selflearn");
  const { t: tc } = useTranslation("common");
  const navigate = useNavigate();
  const location = useLocation();
  const { setSubjectKey } = useTheme();

  const avatarItems = useMemo(
    (): AvatarItem[] => [
      { id: 1, label: tc("profileMenu.myProfile") },
      { id: 2, label: tc("profileMenu.switchProfile") },
      { id: 3, label: tc("profileMenu.switchProduct") },
    ],
    [tc],
  );
  const {
    subjectId,
    subjectName,
    chapterId,
    chapterName,
    examId,
    gradeId,
    subTenantId,
    widgetTitle,
  } = (location.state as TopicSummaryViewAllLocationState | null) ?? {};

  useLayoutEffect(() => {
    if (!subjectName) return;

    setSubjectKey(subjectName);

    return () => setSubjectKey(null);
  }, [setSubjectKey, subjectName]);

  const subjectCrumb = subjectName ?? "";
  const chapterCrumb = chapterName ?? "";
  const isDesktopLayout = !useIsMobile(1024);
  const screenWidth = useScreenWidth();

  const { data: topics, isLoading } =
    useGetExamtocChapterTopicsPublishedVideoAndFlashcardCount(
      {
        chapterId: chapterId ?? 0,
        examId: examId ?? 0,
        subtenantId: subTenantId ?? 0,
      },
      {
        request: { clientName: "cms", paramsSerializer: { indexes: null } },
        query: {
          select: (data) => data.data as Topic[],
          refetchOnMount: true,
          staleTime: 0,
        },
      },
    );

  const { data: topicsProgress } = useGetApiSelflearnGetTopicSummaryProgress(
    {
      examId: String(examId),
      gradeId: String(gradeId),
      subjectId: String(subjectId),
      chapterId: String(chapterId),
    },
    {
      request: { clientName: "selflearn", paramsSerializer: { indexes: null } },
      query: {
        select: (data) => data.data as TopicProgress[],
        refetchOnMount: true,
        refetchOnWindowFocus: true,
        staleTime: 5000,
      },
    },
  );

  const topicSummaryList = useMemo(
    () => buildTopicSummaryList(topics, topicsProgress),
    [topics, topicsProgress],
  );

  const handleSubjectPress = () => {
    navigate(CHAPTER_LIST_HREF, {
      replace: true,
      state: {
        subjectId,
        subjectName,
      },
    });
    return;
  };

  const handleChapterPress = () => {
    navigate(CHAPTER_DETAILS_HREF, {
      replace: true,
      state: {
        chapterId: chapterId,
        chapterName: chapterName,
        subjectId,
        subjectName,
        initialTab: "revise",
      },
    });
    return;
  };

  const breadcrumb = useMemo(
    () => (
      <Breadcrumb>
        <BreadcrumbItem key="learn">
          <BreadcrumbLink
            href={SELF_LEARN_BASE}
            isActive={false}
            onClick={(e) => {
              e.preventDefault();
              navigate(SELF_LEARN_BASE, { replace: true });
            }}
          >
            {t("learnText")}
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator key="sep-1" />
        <BreadcrumbItem key="subject">
          <BreadcrumbLink
            href={CHAPTER_LIST_HREF}
            isActive={false}
            onClick={(e) => {
              e.preventDefault();
              handleSubjectPress();
            }}
          >
            {subjectCrumb}
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator key="sep-2" />
        <BreadcrumbItem key="chapter-details">
          <BreadcrumbLink
            href={CHAPTER_DETAILS_HREF}
            isActive={false}
            onClick={(e) => {
              e.preventDefault();
              handleChapterPress();
            }}
          >
            {chapterCrumb}
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator key="sep-3" />
        <BreadcrumbItem key="topic-summary-all" isCurrentPage>
          <BreadcrumbLink isActive>
            {widgetTitle ?? t("revise.topicSummary.titleFallback")}
          </BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>
    ),
    [navigate, subjectCrumb, chapterCrumb, widgetTitle, t],
  );

  const headerTitle = widgetTitle ?? t("revise.topicSummary.titleFallback");

  return (
    <div className={styles.page}>
      <Box className={styles.headerWrapper}>
        <div className={styles.headerInner}>
          <Header
            title={isDesktopLayout ? undefined : headerTitle}
            leftVariant="back"
            backIcon="circleBackBtn"
            backIconShadow={true}
            backIconWidth={48}
            backIconHeight={48}
            rightIcon={isDesktopLayout ? "userProfileIcon" : undefined}
            rightIconItems={avatarItems}
            onBackPress={() => {
              navigate(-1);
            }}
            breadcrumb={isDesktopLayout ? breadcrumb : undefined}
            titleAlign="center"
            titleColor="var(--colors-neutral-10)"
            titleSize={16}
          />
        </div>
      </Box>

      <div className={styles.content}>
        <div className={styles.gridHost}>
          {isLoading ? (
            <TopicSummaryShimmerGrid
              className={styles.grid}
              screenWidth={screenWidth}
              subjectName={subjectName ?? ""}
            />
          ) : (
            <TopicSummaryGrid
              className={styles.grid}
              topics={topicSummaryList}
              subjectName={subjectName ?? ""}
              screenWidth={screenWidth}
              applyHorizontalPadding={false}
              onTopicPress={
                subjectId != null && chapterId != null && chapterName != null
                  ? (topic) =>
                      navigate(FLASHCARDS_HREF, {
                        state: {
                          subjectId,
                          subjectName: subjectName ?? "",
                          chapterId,
                          chapterName,
                          topicId: topic.topicId,
                          topicName: topic.topicName,
                          flashcardTypeId: 1,
                        },
                      })
                  : undefined
              }
            />
          )}
        </div>
      </div>
    </div>
  );
};
